// Public accept invite page — /invite/:token (KHÔNG cần auth).
// Style match SignupPage: gradient bg + decorative blobs + logo header + card.
//
// Flow:
//   1. Mount → GET /public/invitations/:token → info (workspace name, user_exists, valid)
//   2. Branch:
//      - !valid → error screen (expired/revoked/exhausted)
//      - targeted + user_exists=false → signup form
//      - targeted + user_exists=true → login form
//      - open link → user tự chọn signup hay login (2-tab)
//   3. Submit:
//      - signup → POST /redeem → set JWT → /inbox
//      - login → POST /accept → set JWT → /select-workspace (highlight tenant mới)
//
// ⛔ Đây là cửa VÀO của thợ được mời (nút «Join now» trong thư mời) — người chưa vào được thì
// không có màn nào khác để đọc. Trước 11/10/2026 trang này viết cứng tiếng Việt (khách Canada
// đọc «Link mời đã hết hạn») và chỉ nhận số Việt Nam `^0\d{9}$` trong khi số điện thoại là
// BẮT BUỘC ⇒ thợ Canada/Mỹ KHÔNG nhận lời mời nổi. Chữ nay đi Lingui; số đi luật kernel.

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Trans, useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import toast from 'react-hot-toast';
import { Mail, User, Lock, CheckCircle2, XCircle, LogIn, UserPlus, Loader2, Eye, EyeOff, PartyPopper, Link2 } from 'lucide-react';
// Ô số điện thoại CÓ BỘ CHỌN QUỐC GIA của kernel — cùng ô với màn Đăng ký + Nhân sự. Luật số
// (242 mã quốc gia + độ dài + quy ước lưu: VN `0xxx`, nước khác E.164) nằm ở kernel, SINH từ
// `@cnv/server-shared` — cùng luật với POST /public/invitations/:token/redeem. Đừng viết regex
// số tại trang này nữa (docs/SO_DIEN_THOAI_QUOC_TE.md).
import PhoneInput from '@cnv/ui/common/PhoneInput';
import { isValidPhoneIntl } from '@cnv/ui/utils/phonePolicy';
import { invitationApi } from '../lib/inboxClient';
import { useAuthStore } from '../lib/authStore';
// Nạp catalog `host-admin` — thiếu dòng này thì mọi chuỗi của trang rơi về msgid
// (tức tiếng Việt) ở MỌI ngôn ngữ, mà không có lỗi nào báo.
import '../i18n-host';

// Hằng NGOÀI component: `msg` (chỉ đánh dấu) rồi `i18n._()` ở CHỖ ĐỌC.
const REASON_LABELS = {
  expired: msg`Link mời đã hết hạn`,
  revoked: msg`Link mời đã bị huỷ`,
  exhausted: msg`Link mời đã hết lượt sử dụng`,
};

// Nhãn vai trò dựng theo MÃ `info.role`, không theo `info.role_label` — máy chủ gửi nhãn đó
// bằng tiếng Việt cố định. Cùng msgid với InvitationsPage để dùng chung bản dịch; riêng
// «Nhân viên» có context vì bản không context đang dịch là danh từ số nhiều («Employees»).
const ROLE_LABELS = {
  tenant_owner: msg`Chủ workspace`,
  tenant_admin: msg({ context: 'host', message: `Quản trị` }),
  agent: msg({ message: 'Nhân viên', context: 'vai trò lời mời' }),
  viewer: msg`Người xem`,
};

// Mã lỗi của /redeem + /accept không kèm câu cho người đọc (`data.message`). Mã nào máy chủ đã
// có câu riêng (đã dịch theo Accept-Language) thì câu đó thắng — bảng này chỉ là đường lùi.
const ERROR_LABELS = {
  user_exists: msg`Email này đã có tài khoản — chuyển sang đăng nhập`,
  phone_taken: msg`Số điện thoại này đã được dùng trong workspace`,
  invalid_password: msg`Mật khẩu không đúng`,
  user_disabled: msg`Tài khoản này đã bị khoá — liên hệ admin của workspace`,
  password_not_set: msg`Tài khoản chưa đặt mật khẩu — dùng «Quên mật khẩu» ở màn đăng nhập`,
  user_not_found: msg`Email chưa có tài khoản — hãy tạo tài khoản mới`,
};

/** Câu lỗi cho người đọc: câu máy chủ (đã dịch) → bảng mã ở trên → câu dự phòng. */
function errorText(i18n, e, fallback) {
  const data = e?.response?.data;
  if (typeof data?.message === 'string' && data.message) return data.message;
  const err = data?.error || {};
  const key = ERROR_LABELS[err.code] ? err.code : err.details?.issue;
  if (ERROR_LABELS[key]) return i18n._(ERROR_LABELS[key]);
  // Zod chặn trường số điện thoại (máy chủ chạy cùng luật kernel với ô nhập).
  if ((err.details?.issues || []).some((it) => it?.path === 'phone' || it?.field === 'phone')
      || err.details?.field === 'phone') {
    return i18n._(msg`Số điện thoại không hợp lệ`);
  }
  return fallback;
}

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useLingui();
  const setSession = useAuthStore((s) => s.setSession);

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  // Open link: user tự chọn mode signup hay login (vì BE chưa biết email)
  // Targeted: BE đã biết user_exists → auto branch
  const [openMode, setOpenMode] = useState('signup'); // 'signup' | 'login'

  const [signupForm, setSignupForm] = useState({
    email: '', name: '', password: '', confirmPassword: '', phone: '',
  });
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });

  useEffect(() => {
    let alive = true;
    invitationApi.getInfo(token).then(
      (data) => {
        if (!alive) return;
        setInfo(data);
        setLoading(false);
        // CHỈ prefill name từ suggested_name khi targeted invite.
        // Open link suggested_name = "mô tả nhóm" (vd "Nhóm Sales batch") không phải tên người.
        if (data && !data.is_open_link && data.suggested_name) {
          setSignupForm((f) => ({ ...f, name: data.suggested_name }));
        }
      },
      (e) => {
        if (!alive) return;
        // 404 `invitation` không kèm câu ⇒ để trống, màn lỗi tự dùng câu «Không tìm thấy lời mời».
        setError(typeof e.response?.data?.message === 'string' ? e.response.data.message : '');
        setLoading(false);
      },
    );
    return () => { alive = false; };
  }, [token]);

  async function handleSignup(e) {
    e.preventDefault();
    if (signupForm.password.length < 8) {
      toast.error(t`Mật khẩu cần tối thiểu 8 ký tự`);
      return;
    }
    if (signupForm.password !== signupForm.confirmPassword) {
      toast.error(t`Mật khẩu nhập lại không khớp`);
      return;
    }
    if (!signupForm.phone) {
      toast.error(t`Vui lòng nhập số điện thoại`);
      return;
    }
    if (!isValidPhoneIntl(signupForm.phone)) {
      toast.error(t`Số điện thoại không hợp lệ`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await invitationApi.redeem(token, {
        email: info?.is_open_link ? signupForm.email.trim() : null,
        name: signupForm.name.trim(),
        password: signupForm.password,
        // PhoneInput đã phát đúng quy ước lưu (VN `0xxx`, nước khác `+<mã><số>`); máy chủ vẫn
        // kiểm + chuẩn hoá lại bằng cùng luật kernel.
        phone: signupForm.phone,
      });
      setSession({
        accessToken: res.accessToken,
        refreshToken: res.refreshToken,
        user: res.user,
        active_tenant: res.active_tenant,
      });
      const workspaceName = res.active_tenant?.display_name;
      toast.success(workspaceName ? t`Chào mừng đến ${workspaceName}!` : t`Chào mừng bạn!`);
      navigate('/inbox', { replace: true });
    } catch (err) {
      toast.error(errorText(i18n, err, t`Đăng ký thất bại`));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleLogin(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await invitationApi.accept(token, {
        email: info?.is_open_link ? loginForm.email.trim() : null,
        password: loginForm.password,
      });
      setSession({
        accessToken: res.accessToken,
        refreshToken: res.refreshToken,
        user: res.user,
        active_tenant: res.active_tenant,
      });
      const workspaceName = res.joined_tenant_name;
      toast.success(t`Đã tham gia workspace ${workspaceName}`);
      navigate('/select-workspace', { replace: true, state: { highlight: res.joined_tenant_id } });
    } catch (err) {
      toast.error(errorText(i18n, err, t`Đăng nhập thất bại`));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <PageShell>
        <div className="text-center text-slate-500 py-10 flex items-center justify-center gap-2">
          <Loader2 size={18} className="animate-spin" /> <Trans>Đang kiểm tra lời mời...</Trans>
        </div>
      </PageShell>
    );
  }

  if (error !== null || !info) {
    return (
      <PageShell>
        <ErrorCard title={t`Link không hợp lệ`} message={error || t`Không tìm thấy lời mời`} />
      </PageShell>
    );
  }

  if (!info.valid) {
    return (
      <PageShell>
        <ErrorCard
          title={REASON_LABELS[info.reason] ? i18n._(REASON_LABELS[info.reason]) : t`Link không còn hiệu lực`}
          message={t`Liên hệ admin của workspace để xin link mới.`}
        />
      </PageShell>
    );
  }

  const tenantName = info.tenant_name;
  const inviterName = info.inviter_name;
  const roleLabel = ROLE_LABELS[info.role] ? i18n._(ROLE_LABELS[info.role]) : (info.role_label || info.role);
  const invitedEmail = info.email;

  return (
    <PageShell>
      <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
        {/* Header tenant + role */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto mb-3 bg-pink-50 rounded-2xl flex items-center justify-center">
            <PartyPopper className="w-7 h-7 text-[#A8133D]" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900">
            <Trans>Tham gia <span className="text-[#A8133D]">{tenantName}</span></Trans>
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {inviterName
              ? <Trans><strong>{inviterName}</strong> đã mời bạn với vai trò <strong>{roleLabel}</strong></Trans>
              : <Trans>Bạn được mời với vai trò <strong>{roleLabel}</strong></Trans>}
          </p>
          {invitedEmail ? (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs">
              <CheckCircle2 className="w-3.5 h-3.5" /> <Trans>Email <strong>{invitedEmail}</strong> đã xác thực</Trans>
            </div>
          ) : (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-xs">
              <Link2 className="w-3.5 h-3.5" /> <Trans>Link công khai — bạn tự nhập email</Trans>
            </div>
          )}
        </div>

        {info.is_open_link ? (
          <>
            <div className="grid grid-cols-2 gap-2 mb-5">
              <button type="button" onClick={() => setOpenMode('signup')}
                className={`p-2 text-sm font-medium rounded-md transition ${
                  openMode === 'signup' ? 'bg-blue-100 text-blue-700' : 'text-slate-600 hover:bg-slate-100'
                }`}>
                <UserPlus className="w-4 h-4 inline mr-1" /> <Trans>Tạo tài khoản mới</Trans>
              </button>
              <button type="button" onClick={() => setOpenMode('login')}
                className={`p-2 text-sm font-medium rounded-md transition ${
                  openMode === 'login' ? 'bg-blue-100 text-blue-700' : 'text-slate-600 hover:bg-slate-100'
                }`}>
                <LogIn className="w-4 h-4 inline mr-1" /> <Trans>Đã có tài khoản</Trans>
              </button>
            </div>
            {openMode === 'signup' ? (
              <NewUserForm info={info} form={signupForm} setForm={setSignupForm}
                onSubmit={handleSignup} submitting={submitting} showPwd={showPwd} setShowPwd={setShowPwd} />
            ) : (
              <ExistingUserForm info={info} form={loginForm} setForm={setLoginForm}
                onSubmit={handleLogin} submitting={submitting} />
            )}
          </>
        ) : info.user_exists ? (
          <ExistingUserForm info={info} form={loginForm} setForm={setLoginForm}
            onSubmit={handleLogin} submitting={submitting} />
        ) : (
          <NewUserForm info={info} form={signupForm} setForm={setSignupForm}
            onSubmit={handleSignup} submitting={submitting} showPwd={showPwd} setShowPwd={setShowPwd} />
        )}
      </div>
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-purple-50 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-pink-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-rose-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-6">
          <img src="/siteconnect.png" alt="Site Connect" className="w-20 h-20 mx-auto rounded-2xl shadow-lg mb-4 object-contain" />
          <h1 className="text-2xl font-bold text-slate-900">Site Connect</h1>
          <p className="text-xs font-semibold text-slate-500 mt-1 tracking-widest">
            PROJECTS · SCHEDULING · TIME TRACKING
          </p>
        </div>
        {children}
      </div>
    </div>
  );
}

function NewUserForm({ info, form, setForm, onSubmit, submitting, showPwd, setShowPwd }) {
  const { t } = useLingui();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const phoneInvalid = !!form.phone && !isValidPhoneIntl(form.phone);
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {/* Email */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5"><Trans>Email</Trans></label>
        <div className="relative">
          <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          {info.is_open_link ? (
            <input
              type="email" required autoFocus value={form.email}
              onChange={(e) => set('email', e.target.value)}
              placeholder={t`ban@congty.com`}
              className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          ) : (
            <input
              type="email" readOnly value={info.email}
              className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 text-slate-600 cursor-not-allowed text-sm"
            />
          )}
        </div>
      </div>

      {/* Họ tên */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          <Trans>Họ tên</Trans> <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text" required value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder={t({ context: 'host', message: `Nguyễn Văn A` })}
            className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
      </div>

      {/* Số điện thoại — bắt buộc, số quốc tế theo luật kernel */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          <Trans>Số điện thoại</Trans> <span className="text-red-500">*</span>
        </label>
        <PhoneInput
          value={form.phone}
          onChange={(v) => set('phone', v)}
          invalid={phoneInvalid}
        />
        {phoneInvalid && (
          <p className="text-xs text-red-600 mt-1"><Trans>Số điện thoại không hợp lệ</Trans></p>
        )}
      </div>

      {/* Mật khẩu */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          <Trans>Mật khẩu</Trans> <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type={showPwd ? 'text' : 'password'} required minLength={8} value={form.password}
            onChange={(e) => set('password', e.target.value)}
            className="w-full pl-10 pr-10 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
          <button type="button" onClick={() => setShowPwd((s) => !s)}
            aria-label={showPwd ? t`Ẩn mật khẩu` : t`Hiện mật khẩu`}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-1"><Trans>Tối thiểu 8 ký tự</Trans></p>
      </div>

      {/* Nhập lại mật khẩu */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          <Trans>Nhập lại mật khẩu</Trans> <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type={showPwd ? 'text' : 'password'} required value={form.confirmPassword}
            onChange={(e) => set('confirmPassword', e.target.value)}
            className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
        {form.confirmPassword && form.password !== form.confirmPassword && (
          <p className="text-xs text-red-600 mt-1"><Trans>Mật khẩu nhập lại không khớp</Trans></p>
        )}
      </div>

      <button type="submit" disabled={submitting}
        className="w-full py-2.5 bg-[#A8133D] hover:bg-[#7A0D2C] text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition disabled:opacity-60">
        {submitting && <Loader2 size={16} className="animate-spin" />}
        {submitting ? <Trans>Đang xử lý...</Trans> : <Trans>Tham gia workspace</Trans>}
      </button>
    </form>
  );
}

function ExistingUserForm({ info, form, setForm, onSubmit, submitting }) {
  const { t } = useLingui();
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5"><Trans>Email</Trans></label>
        <div className="relative">
          <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          {info.is_open_link ? (
            <input
              type="email" required autoFocus value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder={t`ban@congty.com`}
              className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          ) : (
            <input
              type="email" readOnly value={info.email}
              className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 text-slate-600 cursor-not-allowed text-sm"
            />
          )}
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          <Trans>Mật khẩu</Trans> <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="password" required autoFocus={!info.is_open_link} value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
      </div>
      <button type="submit" disabled={submitting}
        className="w-full py-2.5 bg-[#A8133D] hover:bg-[#7A0D2C] text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition disabled:opacity-60">
        {submitting && <Loader2 size={16} className="animate-spin" />}
        {submitting ? <Trans>Đang xử lý...</Trans> : <Trans>Đăng nhập + Tham gia</Trans>}
      </button>
    </form>
  );
}

function ErrorCard({ title, message }) {
  return (
    <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center">
      <div className="w-16 h-16 mx-auto mb-4 bg-red-50 rounded-2xl flex items-center justify-center">
        <XCircle className="w-8 h-8 text-red-500" />
      </div>
      <h1 className="text-xl font-bold text-slate-900 mb-2">{title}</h1>
      <p className="text-sm text-slate-600">{message}</p>
    </div>
  );
}
